import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { krw, krwRateLabel } from '@/lib/money'
import { useFx } from '@/lib/queries'
import { cn } from '@/lib/utils'

/** The won rate revenue is shown in beside the dollars. One rate for every
 *  figure, the newest (GET /api/fx `krw_per_usd`): AdMob pays out at its own
 *  rate on the day it pays, so a per-day rate would not give what arrived
 *  either. */
export interface KrwRate {
  perUSD: number
  date?: string
}

const KrwContext = createContext<KrwRate | null>(null)

/** Hands `rate` to every Won under it; null leaves them out. */
export function KrwRateProvider({ rate, children }: { rate: KrwRate | null; children: ReactNode }) {
  return <KrwContext.Provider value={rate}>{children}</KrwContext.Provider>
}

/** The shell's provider: the rate from /api/fx. Until it comes, or when the
 *  server has none, the dollars stand alone. */
export function KrwProvider({ children }: { children: ReactNode }) {
  const fx = useFx()
  const per = fx.data?.krw_per_usd
  const date = fx.data?.krw_date
  const rate = useMemo(() => (per && per > 0 ? { perUSD: per, date } : null), [per, date])
  return <KrwRateProvider rate={rate}>{children}</KrwRateProvider>
}

export function useKrw(): KrwRate | null {
  return useContext(KrwContext)
}

/** A dollar figure in won, muted: "₩16,358". Nothing without a rate. The
 *  rate is in the title. */
export function Won({ usd, className }: { usd: number | null | undefined; className?: string }) {
  const rate = useKrw()
  if (!rate || typeof usd !== 'number' || !Number.isFinite(usd)) return null
  return (
    <span title={krwRateLabel(rate.perUSD, rate.date)} className={cn('whitespace-nowrap tabular-nums text-muted-foreground', className)}>
      {krw(usd, rate.perUSD)}
    </span>
  )
}

/** "원화 환산 1 USD = ₩1,325.57 (2026-10-21)", once per page; nothing
 *  without a rate. */
export function KrwRateNote({ className }: { className?: string }) {
  const rate = useKrw()
  if (!rate) return null
  return <p className={cn('text-meta text-muted-foreground', className)}>원화 환산 {krwRateLabel(rate.perUSD, rate.date)}</p>
}
